import { useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import moment from "moment"
import ActionBar from "@/components/core/ActionBar"
import { APIService } from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { Routes } from "@/routes"
import IncidentStatusBadge from "@/components/Incidents/IncidentStatusBadge"
import IncidentFileGallery from "@/components/Incidents/IncidentFileGallery"
import IncidentCommentThread from "@/components/Incidents/IncidentCommentThread"

const FIELD = ({ label, value }: { label: string; value: any }) => (
  <Col md={6} className="mb-2">
    <span className="text-muted small d-block">{label}</span>
    <span className="fw-semibold">{value ?? "—"}</span>
  </Col>
)

const IncidentDetailPage = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const user: any = parsedUser()

  const [record, setRecord] = useState<any>(null)
  const [files, setFiles] = useState<any[]>([])
  const [creatorName, setCreatorName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const res = await APIService.getAction("Incident/Details", `id=${id}`)
      if (res.data.status === "ok") {
        setRecord(res.data.response.record)
        setFiles(res.data.response.files ?? [])
        setCreatorName(res.data.response.creatorName ?? null)
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [id])

  const handleStatusChange = async (newStatus: string) => {
    setUpdatingStatus(true)
    try {
      const res = await APIService.getAction("Incident/UpdateStatus", `id=${id}&status=${encodeURIComponent(newStatus)}`)
      if (res.data.status === "ok") {
        setRecord((prev: any) => ({ ...prev, status: newStatus }))
        toast.success(t("record saved"))
      }
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.match(/image.*/)) return
    setUploadingFile(true)
    try {
      const formData = new FormData()
      formData.append("path", file)
      const uploadRes = await APIService.postAction("File/Upload", formData)
      const filePath = uploadRes.data?.response
      if (!filePath) return
      const addRes = await APIService.postAction("Incident/Files/Add", {
        incidentId: id, filePath, createdId: user.ID,
      })
      if (addRes.data.status === "ok") {
        setFiles(prev => [...prev, addRes.data.response])
      }
    } finally {
      setUploadingFile(false)
      e.target.value = ""
    }
  }

  if (isLoading) return (
    <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
      <Spinner animation="border" />
    </Container>
  )

  if (!record) return null

  return (
    <Container fluid className="px-4 py-3">
      <ActionBar
        title={`${t("Incidencia")} — #${record.orderNumber}`}
        icon="fas fa-exclamation-circle text-danger"
        actions={
          <Button variant="outline-secondary" size="sm" onClick={() => history.push(Routes.Incidents.path)}>
            <i className="fas fa-arrow-left me-1" />{t("Back")}
          </Button>
        }
      />

      <Row className="my-4">
        <Col lg={8} className="mb-4">
          <Card className="shadow-sm border-0 mb-3">
            <Card.Body>
              <div className="d-flex align-items-center gap-3 mb-3 pb-3 border-bottom">
                <span className="fw-semibold">{t("Status")}:</span>
                <IncidentStatusBadge status={record.status} />
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 180 }}
                  value={record.status}
                  disabled={updatingStatus}
                  onChange={e => handleStatusChange(e.target.value)}
                >
                  <option value="New">{t("Nuevo")}</option>
                  <option value="Under Review">{t("En revisión")}</option>
                  <option value="Resolved">{t("Resuelto")}</option>
                </Form.Select>
                {updatingStatus && <Spinner animation="border" size="sm" />}
              </div>

              <Row>
                <FIELD label={t("Fecha")} value={moment(record.reportDate).format("DD/MM/YYYY")} />
                <FIELD label={t("Creado por")} value={creatorName} />
                <FIELD label={t("Nombre")} value={record.agentName} />
                <FIELD label={t("Dependencia")} value={record.department} />
                <FIELD label={t("Número de pedido")} value={record.orderNumber} />
                <FIELD label={t("Código de cliente")} value={record.customerCode} />
                <FIELD label={t("Nombre del cliente")} value={record.customerName} />
                <FIELD label={t("Almacén del pedido")} value={record.warehouse} />
                <FIELD label={t("Tipo de cliente")} value={record.customerType} />
                <FIELD label={t("Tipo de servicio")} value={record.serviceType} />
                <Col xs={12} className="mb-2">
                  <span className="text-muted small d-block">{t("Tipo de incidencia")}</span>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {record.incidentType?.split(",").map((t: string) => (
                      <Badge key={t} bg="light" text="dark" className="border">{t.trim()}</Badge>
                    ))}
                  </div>
                </Col>
                <Col xs={12} className="mb-2">
                  <span className="text-muted small d-block">{t("Comentarios")}</span>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{record.comments}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-semibold mb-0">{t("Archivos adjuntos")}</h6>
                <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                  {uploadingFile
                    ? <Spinner animation="border" size="sm" />
                    : <><i className="fas fa-paperclip me-1" />{t("Adjuntar archivo")}</>
                  }
                </Button>
              </div>
              <IncidentFileGallery
                files={files}
                canDelete
                onDeleted={fileId => setFiles(prev => prev.filter(f => f.id !== fileId))}
              />
              {files.length === 0 && !uploadingFile && (
                <p className="text-muted small mb-0">{t("Sin archivos")}</p>
              )}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="d-none" onChange={handleFileUpload} />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm border-0" style={{ minHeight: 400 }}>
            <Card.Body className="d-flex flex-column">
              <IncidentCommentThread incidentId={id} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default IncidentDetailPage
